import * as z from 'zod'
import { baseEntitySchema } from '../../core/entity.js'

export const calendarSchema = baseEntitySchema.extend({
  type: z.literal('calendar'),
})
export type Calendar = z.infer<typeof calendarSchema>

export function isCalendar(node?: unknown): node is Calendar {
  return calendarSchema.safeParse(node).success
}

const attendeeSchema = z.object({
  name: z.string(),
  userId: z.string(),
  /** Known only when a member of the calendar created the event: other users' profiles carry no email */
  email: z.string().email().describe('Attendee email, for calendar invites').optional(),
  optional: z.boolean().optional().default(false),
})

export type Attendee = z.infer<typeof attendeeSchema>

/**
 * One occurrence of a series changed on its own (GH #2814, spec R2), keyed by its original start. Cancelled, or with
 * the fields that differ from the series.
 */
export const occurrenceExceptionSchema = z.object({
  cancelled: z.literal(true).describe('The occurrence is cancelled').optional(),
  start: z.iso.datetime().describe('The moved start').optional(),
  end: z.iso.datetime().describe('The moved end').optional(),
  name: z.string().describe('A name for this occurrence only').optional(),
  location: z.string().describe('A location for this occurrence only').optional(),
})
export type OccurrenceException = z.infer<typeof occurrenceExceptionSchema>

/**
 * "Before each event" (GH #2814 M3, spec B1): Nothing, Remind members, or Ask members to confirm, sent {1, 2, 3} days
 * before at the send time (10:00 when absent) in the event's timezone. A check-in also counts as the reminder.
 */
export const beforeEachSchema = z.object({
  level: z.enum(['nothing', 'remind', 'confirm']),
  leadDays: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  sendTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .describe('Local send time, HH:mm')
    .optional(),
})
export type BeforeEach = z.infer<typeof beforeEachSchema>

export const calendarEventSchema = baseEntitySchema.extend({
  type: z.literal('calendarevent'),
  start: z.iso.datetime().nullable(),
  end: z.iso.datetime().nullable().optional(),
  allDay: z.boolean().default(false).optional(),
  location: z.string().optional(),
  organizer: attendeeSchema.optional(),
  attendees: z.array(attendeeSchema).optional(),
  /** GH #2814: an RFC 5545 RRULE without DTSTART (the event's start is it); occurrences are expanded on read (R1) */
  recurrence: z.string().describe('RRULE, e.g. FREQ=WEEKLY;BYDAY=TH').optional(),
  /** The organizer's IANA timezone: a series keeps its wall-clock time across DST. Missing: UTC */
  timezone: z.string().describe('IANA timezone, e.g. Europe/Oslo').optional(),
  /** Occurrences changed on their own, keyed by their original start (iCal RECURRENCE-ID, R2) */
  exceptions: z
    .record(z.iso.datetime(), occurrenceExceptionSchema)
    .describe('Per-occurrence changes, keyed by original start')
    .optional(),
  /** iCal SEQUENCE: increases with every change (R5) */
  sequence: z.number().int().min(0).describe('Revision, for iCal').optional(),
  /**
   * GH #2814 M2, "This and following": a split-off series names the series it came from, and that series, once ended,
   * names it. Members hear about the split when the old series is ended, so a split that fails and is rolled back is
   * never announced.
   */
  splitFrom: z.string().describe('The series this one was split off from').optional(),
  splitInto: z.string().describe('The series split off from this one, the latest split').optional(),
  /**
   * A split-off series that was never announced: the page sets it before removing the series when ending the old one
   * failed, so its delete is silent whatever the old series looks like by then (Quincy's review of #2936).
   */
  splitRolledBack: z.boolean().describe('A split-off series removed before it was announced').optional(),
  /** GH #2814 M3: "Before each event" (B1–B3). Absent means Nothing, the default for a new event */
  beforeEach: beforeEachSchema.describe('Reminders or check-ins before each occurrence').optional(),
})
export type CalendarEvent = z.infer<typeof calendarEventSchema>

export function isCalendarEvent(node?: unknown): node is CalendarEvent {
  return calendarEventSchema.safeParse(node).success
}
